<template>
  <v-container>
    <v-card class="mx-auto pa-8 mt-16" max-width="440px">
      <div class="d-flex justify-center">
        <div><v-img src="@/assets/nexus-logo.png" max-width="50"></v-img></div>
        <div class="ml-6">
          <span class="text-h3"> N E X U S </span>
          <div class="font-weight-light text-right">Scheduler</div>
        </div>
      </div>

      <v-card-text>
        <div class="text-h6 my-6">Sign in to continue</div>

        <span v-if="account && account.id">
          <v-alert dense type="info">
            <v-row class="no-gutters">
              <v-col class="grow">
                Selected account:<br />
                {{ account.name }}
              </v-col>
              <v-col class="shrink ma-0">
                <v-btn @click="account = {}" icon>
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </span>
        <span v-if="$route.query.msg && $route.query.msg">
          <v-alert dense type="warning">
            {{ $route.query.msg }}
          </v-alert>
        </span>
        <v-form ref="form" lazy-validation>
          <v-text-field
            :rules="[rules.required]"
            tabindex="1"
            label="Enter your email or user name"
            v-model="email"
            data-cy="loginemail"
          ></v-text-field>
          <v-text-field
            tabindex="2"
            :rules="[rules.required]"
            v-model="password"
            data-cy="loginpassword"
            name="name"
            label="Enter your password"
            v-on:keyup.enter="loginLocal"
            :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="() => (showPass = !showPass)"
            :type="showPass ? 'text' : 'password'"
          ></v-text-field>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          tabindex="3"
          class="px-10"
          color="primary"
          data-cy="loginsubmit"
          @click.prevent="standardLogin"
          :disabled="isAppLoading"
          >Login</v-btn
        >
        <v-spacer />
      </v-card-actions>
      <p class="text-center ma-0 mt-4 caption">
        <v-btn text small color="primary" @click="forgotAccoutDialog = true"
          >Forgot Password</v-btn
        >
      </p>
      <v-divider class="my-5" />
      <v-card-actions>
        <v-spacer />
        <google-sign-in-button
          @onSuccess="onGoogleSignIn"
          :oneTap="false"
          :clientId="googleClientId"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>

    <v-dialog v-model="multipleAccountDialog" max-width="600px">
      <v-card class="pa-8">
        <v-card-title>Multiple accounts detected</v-card-title>
        <v-card-subtitle
          >Choose an account below to continue with sign in</v-card-subtitle
        >
        <v-card-text>
          <v-radio-group v-model="account">
            <v-radio
              v-for="acct in accountOptions"
              :key="acct.id"
              :label="acct.name"
              :value="acct"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="multipleAccountDialog = false">Cancel</v-btn>
          <v-btn @click="changeAccount" color="primary">Select Account</v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="forgotAccoutDialog" max-width="600px">
      <v-card class="pa-8">
        <v-card-title class="pt-0">Retrieve Password</v-card-title>
        <v-card-subtitle
          >Please enter your email below to reset your password</v-card-subtitle
        >
        <v-card-text>
          <v-text-field
            v-model="resetPwdEmail"
            label="Enter your email"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="forgotAccoutDialog = false">Cancel</v-btn>
          <v-btn @click="resetPassword" color="primary">Reset Password</v-btn>
          <v-spacer />
        </v-card-actions>
        <!-- <div class="`text-sm text-center text--secondary">
        A reset password email will be sent to you
      </div> -->
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { rules } from "@/store/util/rules.js";

export default {
  data() {
    return {
      email: "",
      password: "",
      googleOauthToken: null,
      account: {},
      showPass: false,
      accountOptions: [],
      multipleAccountDialog: false,
      forgotAccoutDialog: false,
      resetPwdEmail: null,
      rules,
      googleClientId: process.env.VUE_APP_GOOGLE_CLIENT_ID,
    };
  },
  computed: {
    ...mapGetters(["currentUser", "isAppLoading"]),
  },
  mounted() {
    // Get 'token' parameter from URL, if it exists
    const tokenFromUrl = this.$route.query.token;

    // logout first if currently logged and token from url is not empty
    if( this.currentUser && tokenFromUrl ){
      this.processLogout();
      this.clearCenter();
    }

    //Check if user is already logged in
    if (this.currentUser) {
      this.redirectLoggedIn();
    } 
    else if(tokenFromUrl){
      this.remoteJwtTokenCheck(tokenFromUrl)
        .then( (resp) => {
          if (resp.data && resp.data.jwttoken) {
            this.updateLocalStorage(resp);
            this.redirectLoggedIn();
          }
        })
    }
    else {
      const initialAccount = this.$route.query.account;
      this.loadAccountInfo(initialAccount).then((acct) => {
        this.account = acct;
      });
    }
  },
  methods: {
    ...mapActions([
      "addErrorMessage",
      "addSuccessMessage",
      "clearMessage",
      "updateUserToken",
      "clearCenter",
      "remoteJwtTokenCheck",
      "processLogout",
    ]),
    standardLogin() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.googleOauthToken = null;
      this.loginLocal();
    },
    loginLocal() {
      let user = {
        username: this.email,
        password: this.password,
        useCookie: true,
        accountId: this.account && this.account.id ? this.account.id : null,
        googleAuthToken: this.googleOauthToken,
        role: "Administrator",
        module: process.env.VUE_APP_MODULE,
      };
      this.login(user)
        .then(() => {
          this.clearMessage();
          //redirect logged in
          this.redirectLoggedIn();
        })
        .catch((error) => {
          if (error.status == 403) {
            //Forbidden
            this.addErrorMessage(
              "Please contact your administrator for access to this module"
            );
          }
          if (error.status == 401) {
            //Unauthorized
            this.addErrorMessage("Invalid Credentials");
          } else if (error.status == 500) {
            this.addErrorMessage(
              "Server network error, please try again later"
            );
          }
        });
    },
    login(credential) {
      return this.standardAuthentication(credential);
    },
    standardAuthentication(credential) {
      return new Promise((resolve, reject) => {
        this.$store.commit("auth_request", null, { root: true });
        this.$axios
          .post("authenticate", credential, {
            headers: {
              "content-type": "application/json",
            },
          })
          .then((resp) => {
            if (resp.data && resp.data.multipleAccounts) {
              this.multipleAccountDialog = true;
              this.accountOptions = resp.data.accounts;
              this.account = this.accountOptions[0];
            } else if (resp.data && resp.data.jwttoken) {
              this.updateLocalStorage(resp);
              resolve(resp);
            } else {
              console.log("Unknown response...");
              console.log(resp);
            }
          })
          .catch((err) => {
            this.$store.commit("auth_error");
            localStorage.removeItem("token");
            reject(err);
          });
      });
    },
    onGoogleSignIn({ idToken, email }) {
      this.email = email;
      this.googleOauthToken = idToken;

      this.loginLocal();
    },
    updateLocalStorage(resp) {
      this.clearCenter();

      const token = resp.data.jwttoken;
      const user = {
        email: resp.data.email,
        firstName: resp.data.firstName,
        lastName: resp.data.lastName,
        company: resp.data.companyName,
        gsuiteAPIAvailable: resp.data.gsuiteAPIAvailable,
        uId: resp.data.uid,
        confirmed: resp.data.confirmed,
        roles: resp.data.roles,
        modules: resp.data.modules,
      };

      this.updateUserToken({
        vm: this,
        auth: {
          token: token,
          user: user,
        },
      });
    },
    changeAccount() {
      this.multipleAccountDialog = false;
      this.loginLocal();
    },
    resetPassword() {
      const params = {
        email: this.resetPwdEmail,
        module: process.env.VUE_APP_MODULE,
        port: process.env.VUE_APP_PORT,
      };
      this.$axios
        .post("/public/user/send-reset-password-email", null, { params })
        .then(() => {
          this.addSuccessMessage("Password reset email sent");
        })
        .catch((error) => {
          this.addErrorMessage(error.data.message || "Account not found");
        });
      this.forgotAccoutDialog = false;
    },
    async loadAccountInfo(accountId) {
      if (accountId) {
        return await this.$axios
          .get(`/public/account/${accountId}/info`)
          .then((resp) => {
            this.account = resp.data;
            return this.account;
          })
          .catch(() => {
            return null;
          });
      } else {
        return null;
      }
    },
    redirectLoggedIn() {
      const confirmed = this.currentUser.confirmed;
      if (confirmed) {
        this.$router.push(this.$route.query.redirect || "/").catch(() => {});
      } else {
        this.$router.push({
          name: "ResetPassword",
        });
      }
    },
  }
};
</script>
